import {MouseEventHandler, PropsWithChildren, RefCallback, type FC} from "react"
import {
  Stack,
  Typography,
  IconButton as IconButtonMUI,
  IconButtonProps,
} from "@mui/material"
import Joyride, {Step, StylesOptions} from "react-joyride"
import {CloseIcon} from "../../../assets"
import {ArrowDropUp} from "@mui/icons-material"

export type NewFeatureBodyProps = {
  size: number
  continuous: boolean
  index: number
  step: Step
  backProps: {
    "aria-label": string
    "data-action": string
    onClick: MouseEventHandler<HTMLElement>
    role: string
    title: string
  }
  closeProps: {
    "aria-label": string
    "data-action": string
    onClick: MouseEventHandler<HTMLElement>
    role: string
    title: string
  }
  primaryProps: {
    "aria-label": string
    "data-action": string
    onClick: MouseEventHandler<HTMLElement>
    role: string
    title: string
  }
  tooltipProps: {
    "aria-modal": boolean
    ref: RefCallback<HTMLElement>
    role: string
  }
  onClose: () => void
}

const IconButton: FC<PropsWithChildren<IconButtonProps>> = (props) => {
  const {children} = props
  return (
    <IconButtonMUI
      {...props}
      disableRipple={true}
      sx={{
        height: "40px",
        widht: "40px",
        color: props.disabled ? "#9b9b9b" : "#fff",
        borderRadius: "4px",
        borderStyle: "solid",
        borderWidth: "1px",
        backgroundColor: props.disabled ? "#eeeeee" : "#053747",
        borderColor: props.disabled ? "#bdbdbc" : "#053747",
        "&:hover": {
          backgroundColor: "#0A495D",
        },
      }}
    >
      {children}
    </IconButtonMUI>
  )
}

const NewFeatureRideBody = ({
  size,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  onClose,
}: NewFeatureBodyProps) => {
  const getBorderDecoration = (step: Step) => {
    if (
      step.placement === "top" ||
      step.placement === "top-end" ||
      step.placement === "top-start"
    ) {
      return {
        borderTop: "unset",
        borderRight: "unset",
        borderBottom: "4px solid #22c3bb",
        borderLeft: "unset",
      }
    }

    if (
      step.placement === "right" ||
      step.placement === "right-end" ||
      step.placement === "right-start"
    ) {
      return {
        borderRight: "unset",
        borderTop: "unset",
        borderBottom: "unset",
        borderLeft: "4px solid #22c3bb",
      }
    }

    if (
      step.placement === "bottom" ||
      step.placement === "bottom-end" ||
      step.placement === "bottom-start"
    ) {
      return {
        borderBottom: "unset",
        borderRight: "unset",
        borderTop: "4px solid #22c3bb",
        borderLeft: "unset",
      }
    }

    if (
      step.placement === "left" ||
      step.placement === "left-end" ||
      step.placement === "left-start"
    ) {
      return {
        borderLeft: "unset",
        borderRight: "4px solid #22c3bb",
        borderBottom: "unset",
        borderTop: "unset",
      }
    }

    return {
      borderLeft: "unset",
      borderRight: "unset",
      borderBottom: "unset",
      borderTop: "unset",
    }
  }

  return (
    <Stack position={"relative"} marginRight={"26px"}>
      <Stack position={"absolute"} right={"8px"} top={"-14px"}>
        <ArrowDropUp
          sx={{
            color: "#22c3bb",
            stroke: "#22c3bb",
            strokeWidth: 2,
            strokeLinecap: "round",
          }}
        />
      </Stack>
      <Stack
        {...tooltipProps}
        gap="16px"
        sx={{
          backgroundColor: "#f4f4f4",
          padding: "16px",
          ...getBorderDecoration(step),
          borderRadius: "4px",
          maxWidth: "272px",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontSize="16px" fontWeight={600} color="#242d35">
            {step.title}
          </Typography>
          <IconButton
            {...closeProps}
            onClick={() => onClose()}
            style={{
              backgroundColor: "unset !important",
              borderColor: "unset !important",
              border: "none",
              height: "unset",
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: "6px",
              paddingRight: "6px",
            }}
          >
            <CloseIcon width={"18px"} height={"18px"} />
          </IconButton>
        </Stack>
        <Typography fontSize="14px" color={"#242d35b3"}>
          {step.content}
        </Typography>
      </Stack>
    </Stack>
  )
}

export type NewFeatureRideProps = {
  run?: boolean
  setRun?: (run: boolean) => void
  steps: Step[]
  continuous?: boolean
  stylesOptions?: Partial<StylesOptions>
  onClose: () => void
}

const NewFeatureRide: FC<NewFeatureRideProps> = (props) => {
  const {run, steps, continuous, stylesOptions, onClose} = props

  return (
    <Joyride
      run={run}
      steps={[...steps]}
      continuous={continuous}
      callback={(data) => {
        if (
          data.action === "close" &&
          data.index + 1 === data.size &&
          data.lifecycle === "complete"
        ) {
          onClose()
        }
      }}
      tooltipComponent={(e) => NewFeatureRideBody({...e, onClose})}
      disableScrollParentFix={true}
      disableScrolling={true}
      floaterProps={{
        hideArrow: true,
        style: {
          left: "-26px",
        },
      }}
      styles={{
        spotlight: {
          borderRadius: 0,
        },
        options: {
          arrowColor: "#22c3bb",
          zIndex: 9999,
          ...stylesOptions,
        },
      }}
    />
  )
}

export default NewFeatureRide
